import {
  type FC,
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  type DashboardOrganizationSummary,
  createOrganizationAsUser,
  listOrganizationsAsUser,
  selectOrganizationAsUser,
} from "../../dashboard-client";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { Button } from "../common/Button";
import { FormInput } from "../common/forms/FormInput";

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

export const OrgSwitcher: FC = () => {
  const auth = useDashboardAuth();
  const [orgs, setOrgs] = useState<DashboardOrganizationSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void listOrganizationsAsUser()
      .then((res) => setOrgs(res.data.organizations))
      .catch(() => {
        /* Non-fatal */
      });
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) {
      return;
    }
    function handleClick(event: MouseEvent) {
      const { target } = event;
      if (!(target instanceof Node)) {
        return;
      }
      if (ref.current && !ref.current.contains(target)) {
        setOpen(false);
        setShowCreate(false);
        setCreateError(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const currentOrg = orgs.find(
    (o) => o.organization_id === auth.organizationId,
  );

  const handleSwitch = useCallback(
    async (orgId: string) => {
      if (orgId === auth.organizationId || switching) {
        return;
      }
      setSwitching(true);
      try {
        await selectOrganizationAsUser({ body: { organization_id: orgId } });
        await auth.refreshSession();
        window.location.reload();
      } catch {
        setSwitching(false);
      }
    },
    [auth, switching],
  );

  const handleCreate = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const trimmedName = newName.trim();
      const trimmedSlug = newSlug.trim() || toSlug(trimmedName);
      if (!trimmedName || !trimmedSlug) {
        return;
      }
      setCreating(true);
      setCreateError(null);
      try {
        const createOrgResponse = await createOrganizationAsUser({
          body: { name: trimmedName, slug: trimmedSlug },
        });
        const org = createOrgResponse.data;
        await selectOrganizationAsUser({
          body: { organization_id: org.organization_id },
        });
        await auth.refreshSession();
        window.location.assign("/onboarding");
      } catch (error) {
        setCreateError(
          error instanceof Error
            ? error.message
            : "Failed to create organization.",
        );
        setCreating(false);
      }
    },
    [newName, newSlug, auth],
  );

  const openCreateForm = useCallback(() => {
    setShowCreate(true);
    setNewName("");
    setNewSlug("");
    setSlugTouched(false);
    setCreateError(null);
  }, []);

  const handleNameChange = useCallback(
    (value: string) => {
      setNewName(value);
      if (!slugTouched) {
        setNewSlug(toSlug(value));
      }
    },
    [slugTouched],
  );

  const handleSlugChange = useCallback((value: string) => {
    setSlugTouched(true);
    setNewSlug(value);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between border border-slate-300 px-2 py-2 text-left transition-colors hover:border-slate-400 hover:bg-slate-100/60 dark:border-slate-700 dark:hover:border-slate-600 dark:hover:bg-slate-800/40"
      >
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-500">
            <span aria-hidden className="text-slate-300 dark:text-slate-700">
              [
            </span>
            <span>Organization</span>
            <span aria-hidden className="text-slate-300 dark:text-slate-700">
              ]
            </span>
          </p>
          <p className="mt-0.5 truncate font-mono text-[12px] font-bold uppercase tracking-widest text-slate-900 dark:text-slate-100">
            {currentOrg?.name ?? "Select org"}
          </p>
          {currentOrg && (
            <p className="truncate font-mono text-[9px] uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
              {currentOrg.slug}
            </p>
          )}
        </div>
        <svg
          className={`ml-1 h-3 w-3 flex-shrink-0 text-slate-400 transition-transform dark:text-slate-500 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-full border border-slate-300 bg-white py-1 shadow-sm dark:border-slate-700 dark:bg-arti-panel">
          {orgs.map((org) => {
            const isActive = org.organization_id === auth.organizationId;
            return (
              <button
                key={org.organization_id}
                type="button"
                disabled={switching}
                onClick={() => void handleSwitch(org.organization_id)}
                className={`flex w-full items-center gap-2 border-l-2 px-3 py-2 text-left font-mono text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50 ${
                  isActive
                    ? "border-emerald-500/60 bg-emerald-500/5 font-bold text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-500/[0.06] dark:text-emerald-300"
                    : "border-transparent text-slate-500 hover:border-slate-300 hover:bg-slate-100/60 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-700 dark:hover:bg-slate-800/40 dark:hover:text-slate-200"
                }`}
              >
                <span className="hidden" aria-hidden>
                  {org.organization_id === auth.organizationId
                    ? "\u25CF"
                    : "\u25CB"}
                </span>
                <span className="min-w-0 flex-1 truncate">{org.name}</span>
              </button>
            );
          })}

          <div className="border-t border-gray-200 pt-1 dark:border-white/10">
            {showCreate ? (
              <form
                onSubmit={(e) => void handleCreate(e)}
                className="space-y-2 px-3 py-2"
              >
                <FormInput
                  type="text"
                  placeholder="Organization name"
                  value={newName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  disabled={creating}
                  autoFocus
                />
                <FormInput
                  type="text"
                  placeholder="slug"
                  value={newSlug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  disabled={creating}
                />
                {createError && (
                  <p className="text-xs text-red-500">{createError}</p>
                )}
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={creating || !newName.trim()}
                    variant="primary"
                    size="sm"
                    className="flex-1"
                  >
                    {creating ? "Creating…" : "Create"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowCreate(false)}
                    disabled={creating}
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 hover:text-gray-700 dark:text-white/50 dark:hover:text-white/70"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <Button
                type="button"
                onClick={openCreateForm}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-left text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-white/50 dark:hover:bg-white/5 dark:hover:text-white/70"
              >
                <span className="text-xs">+</span>
                <span>New organization</span>
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
